import React, { useEffect } from "react";
import { Route, RouteProps, useHistory, useLocation } from "react-router-dom";

import * as ROUTES from "common/routes.constants";
import { useOrganizations } from "contexts/Organizations";
import { FullpageSpinner } from "components";
import useAuth from "../../contexts/Auth/useAuth";

const ProtectedRoute = (props: RouteProps) => {
	const history = useHistory();
	const { authenticated } = useAuth();
	const { search, pathname } = useLocation();
	const { member, currentOrganizationPlan, current } = useOrganizations();
	const { location: nextLocation } = props;

	const roleLevels = {
		OWNER_LEVEL: ["owner"],
		ADMIN_LEVEL: ["owner", "admin"],
		SENIOR_LEVEL: ["owner", "admin", "senior"],
		JUNIOR_LEVEL: ["owner", "admin", "senior", "junior"],
		GUEST_LEVEL: ["owner", "admin", "senior", "junior", "guest"],
	};

	const pagesRole = [
		{
			pathname: `/${ROUTES.ADMIN}`,
			minimumRoleLevelNeeded: [...roleLevels.ADMIN_LEVEL],
		},
		{
			pathname: `/${ROUTES.ADMIN}/${ROUTES.ORGANIZATION}`,
			minimumRoleLevelNeeded: roleLevels.ADMIN_LEVEL,
		},
		{
			pathname: `/${ROUTES.ADMIN}/${ROUTES.MEMBERS}`,
			minimumRoleLevelNeeded: roleLevels.ADMIN_LEVEL,
		},
		{
			pathname: `/${ROUTES.ADMIN}/${ROUTES.MANAGE_PROJECTS}`,
			minimumRoleLevelNeeded: roleLevels.ADMIN_LEVEL,
		},
		{
			pathname: `/${ROUTES.ADMIN}/${ROUTES.SUBSCRIPTION}`,
			minimumRoleLevelNeeded: roleLevels.ADMIN_LEVEL,
		},
		{
			pathname: `/${ROUTES.ADMIN}/${ROUTES.STATISTICS}`,
			minimumRoleLevelNeeded: roleLevels.ADMIN_LEVEL,
		},
	];

	// Everything under /admin manages an organization: its members, its
	// subscription, its profile, which projects belong to whom. A personal space
	// has none of that — one implicit member, no plan of its own, one project —
	// so those pages are meaningless there, and the owner role the user holds
	// over their own personal org would otherwise wave them straight through.
	// The menu entry is hidden in UserMenu; this closes the URL, so neither a
	// typed address, a bookmark from a business org, nor an org switch made
	// while already on an admin page can land anyone in there.
	const blockedForPersonalOrganization = Boolean(
		current?.isPersonal &&
			nextLocation?.pathname.startsWith(`/${ROUTES.ADMIN}`),
	);

	const userGranted = (userRole: any) => {
		const nextPage = pagesRole.find(
			(page: { pathname: string; minimumRoleLevelNeeded: string[] }) => {
				return nextLocation?.pathname.includes(page.pathname);
			},
		);

		/* If user has the minimum role required for the page, show it.
		   If nextPage is null, it means that the nextlocation/nextPage has no role protection. */
		return nextPage
			? nextPage?.minimumRoleLevelNeeded.includes(userRole)
			: true;
	};

	const handleExpiredSubscriptionRedirect = () => {
		if (currentOrganizationPlan?.isExpired) {
			if (
				nextLocation &&
				["/admin/subscription"].includes(nextLocation?.pathname)
			) {
				if (member?.role === "owner") {
					history.push({
						pathname: nextLocation.pathname,
						search,
						state: { destinationPath: pathname },
					});
				} else {
					history.push({
						pathname: `/${ROUTES.EXPIRED_SUBSCRIPTION}`,
						search,
						state: { destinationPath: pathname },
					});
				}
			} else {
				history.push({
					pathname: `/${ROUTES.EXPIRED_SUBSCRIPTION}`,
					search,
					state: { destinationPath: pathname },
				});
			}
		}
	};

	// Authentication protection.
	if (!authenticated) {
		history.push({
			pathname: `/${ROUTES.LOGIN}`,
			search,
			state: { destinationPath: pathname },
		});
	}

	useEffect(() => {
		// Role protection, redirect to /projects if user has no rights.
		if (member) {
			if (!userGranted(member.role) || blockedForPersonalOrganization) {
				history.push({
					pathname: `/${ROUTES.PROJECTS}`,
					search,
					state: { destinationPath: pathname },
				});
			}
		}

		// current is in here, not just member: switching to the personal space
		// while sitting on an admin page has to redirect too, and that changes
		// the organization without changing the membership record.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [member, blockedForPersonalOrganization]);

	useEffect(() => {
		handleExpiredSubscriptionRedirect();

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [currentOrganizationPlan, pathname]);

	// Show spinner (before redirect to /projects) if user has no rights to see
	// the page — the redirect above runs in an effect, so without this the page
	// would render once first.
	if (!userGranted(member?.role) || blockedForPersonalOrganization) {
		return <FullpageSpinner />;
	}

	return <Route {...props} />;
};

export default ProtectedRoute;
